/**
 * Loja VIP (10.6S): tiers VIP com vantagens + pacotes de diamantes.
 * Inspiração: tela de Privilégios VIP com paginação entre tiers e lista de perks.
 */
import { bagUpgradeInfo } from "../items/BagUpgrade";
import { GameConfig } from "../config/GameConfig";
import { i18n } from "../i18n/i18n";
import type { GameProgress } from "../progression/GameProgress";
import { Ads } from "../ads/Ads";
import { Vip } from "../progression/Vip";

export interface VipHandlers {
  onClose: () => void;
  onBuyPack: (packId: string) => void;
  onBuyItem: (itemId: string) => void;
  onBuyAttr: (itemId: string) => void;
  onBuyPass: () => void;
  /** 10.9BE — recompensa por anúncio (💎 grátis / booster de XP) */
  onAd: (placement: "freeDiamonds" | "xpBoost") => void;
}

export class VipScreen {
  private readonly root: HTMLDivElement;
  private readonly h: VipHandlers;
  private lastP: GameProgress | null = null;
  private viewTier = 0;
  private readonly collapsedCats = new Set<string>(); // 10.9DI — categorias recolhidas da loja de diamantes

  constructor(uiRoot: HTMLElement, h: VipHandlers) {
    this.h = h;
    this.root = document.createElement("div");
    this.root.className = "overlay";
    // 10.9AW — tocar FORA do painel fecha o VIP (pedido do Thiago)
    this.root.addEventListener("click", (e) => { if (e.target === this.root) this.h.onClose(); });
    this.root.hidden = true;
    this.root.innerHTML = `<div class="panel vip-panel"></div>`;
    uiRoot.appendChild(this.root);
    i18n.onChange(() => { if (!this.root.hidden) this.render(); });
  }

  open(p: GameProgress): void {
    this.lastP = p;
    this.viewTier = p.vipTier;
    if (this.collapsedCats.size === 0) { // 10.9DJ — abre só a 1ª categoria da loja de diamantes
      const cats: string[] = [];
      for (const it of GameConfig.diamondShop.items) { const c = (it as { cat?: string }).cat ?? ""; if (c && !cats.includes(c)) cats.push(c); }
      cats.slice(1).forEach((c) => this.collapsedCats.add(c));
    }
    this.render();
    this.root.hidden = false;
  }

  hide(): void { this.root.hidden = true; }

  refresh(p: GameProgress): void {
    this.lastP = p;
    if (!this.root.hidden) this.render();
  }

  private render(): void {
    const p = this.lastP;
    if (!p) return;
    const panel = this.root.querySelector(".vip-panel")!;
    const tiers = GameConfig.vipTiers;
    const cur = p.vipTier;
    const vt = Math.max(0, Math.min(tiers.length - 1, this.viewTier));

    let html = `
      <div class="vip-head">
        <h2 class="vip-title">${this.esc(i18n.t("vip.shopTitle"))}</h2>
        <div class="vip-res">
          <span class="vip-diamonds">💎 <b>${p.diamonds}</b></span>
          <span class="vip-coins">🪙 <b>${p.coins}</b></span>
        </div>
        <button class="btn scr-x" data-a="close" aria-label="fechar">✕</button>
      </div>`;

    // current tier status
    const tierName = cur > 0 ? i18n.t(tiers[cur - 1].nameKey) : i18n.t("vip.noTier");
    const tierIcon = cur > 0 ? tiers[cur - 1].icon : "—";
    html += `<div class="vip-current">${tierIcon} ${this.esc(tierName)}</div>`;
    // 10.9BG — barra de PONTOS VIP até o próximo tier (💎 gastos/comprados = pontos; sobe sozinho)
    const nextTier = cur < tiers.length ? tiers[cur] : null;
    const prevPts = cur > 0 ? tiers[cur - 1].cost : 0;
    const frac = nextTier ? Math.max(0, Math.min(1, (p.vipPoints - prevPts) / (nextTier.cost - prevPts))) : 1;
    html += `<div class="vip-points">
      <div class="vip-points-row"><span>${this.esc(i18n.t("vip.points", { n: String(p.vipPoints) }))}</span><span>${nextTier ? this.esc(i18n.t("vip.pointsNext", { n: String(nextTier.cost), tier: i18n.t(nextTier.nameKey) })) : this.esc(i18n.t("vip.pointsMax"))}</span></div>
      <div class="vip-points-bar"><i style="width:${Math.round(frac * 100)}%"></i></div>
      <div class="vip-points-hint">${this.esc(i18n.t("vip.pointsHint"))}</div>
    </div>`;

    // tier viewer with arrows
    const tier = tiers[vt];
    const owned = vt < cur;
    const isCurrent = vt + 1 === cur;

    html += `<div class="vip-tier-nav">
      <button class="vip-arrow vip-prev"${vt <= 0 ? " disabled" : ""}>◀</button>
      <div class="vip-tier-label">
        <span class="vip-tier-icon">${tier.icon}</span>
        <span class="vip-tier-name">${this.esc(i18n.t(tier.nameKey))}</span>
        <span class="vip-tier-sub">${owned || isCurrent ? i18n.t("vip.acquired") : this.esc(i18n.t("vip.pointsReq", { n: String(tier.cost) }))}</span>
      </div>
      <button class="vip-arrow vip-next"${vt >= tiers.length - 1 ? " disabled" : ""}>▶</button>
    </div>`;

    // perks list
    html += `<div class="vip-perks">`;
    const pk = tier.perks;
    const prevPk = vt > 0 ? tiers[vt - 1].perks : null;

    const perkRows: { label: string; tag: "NOVO" | "UP" | "✓" }[] = [];
    if (pk.xpMult > 1) {
      const pct = Math.round((pk.xpMult - 1) * 100);
      const isUp = prevPk && prevPk.xpMult > 1;
      perkRows.push({ label: i18n.t("vip.perk.xp", { pct: String(pct) }), tag: isUp ? "UP" : "NOVO" });
    }
    if (pk.coinMult > 1) {
      const pct = Math.round((pk.coinMult - 1) * 100);
      const isUp = prevPk && prevPk.coinMult > 1;
      perkRows.push({ label: i18n.t("vip.perk.coins", { pct: String(pct) }), tag: isUp ? "UP" : "NOVO" });
    }
    if (pk.essenceMult > 1) {
      const pct = Math.round((pk.essenceMult - 1) * 100);
      const isUp = prevPk && prevPk.essenceMult > 1;
      perkRows.push({ label: i18n.t("vip.perk.essence", { pct: String(pct) }), tag: isUp ? "UP" : "NOVO" });
    }
    if (pk.absorbMult > 1) {
      const pct = Math.round((pk.absorbMult - 1) * 100);
      const isUp = prevPk && prevPk.absorbMult > 1;
      perkRows.push({ label: i18n.t("vip.perk.absorb", { pct: String(pct) }), tag: isUp ? "UP" : "NOVO" });
    }
    // 10.9L0 — slot de arma VIP VOLTOU (decisão do Thiago): +1 arma atacando na Corrente
    if ((pk as { weaponSlot?: boolean }).weaponSlot) {
      const isUp = !!(prevPk as { weaponSlot?: boolean } | undefined)?.weaponSlot;
      perkRows.push({ label: i18n.t("vip.perk.weaponSlot"), tag: isUp ? "✓" : "NOVO" }); // o mesmo em todo tier: "✓" = continua
    }
    if (pk.bagBonus > 0) {
      const isUp = prevPk && prevPk.bagBonus > 0;
      perkRows.push({ label: i18n.t("vip.perk.bagBonus", { n: String(pk.bagBonus) }), tag: isUp ? "UP" : "NOVO" });
    }
    if (pk.dropRarityBoost) {
      const isUp = prevPk?.dropRarityBoost;
      perkRows.push({ label: i18n.t("vip.perk.dropBoost"), tag: isUp ? "UP" : "NOVO" });
    }
    if (pk.dailyDiamonds > 0) {
      const isUp = prevPk && prevPk.dailyDiamonds > 0;
      perkRows.push({ label: i18n.t("vip.perk.dailyDiamonds", { n: String(pk.dailyDiamonds) }), tag: isUp ? "UP" : "NOVO" });
    }
    // areas
    const areas = tier.areas;
    if (areas.length > 0) {
      const isUp = prevPk && vt > 0 && tiers[vt - 1].areas.length > 0;
      perkRows.push({ label: i18n.t("vip.perk.areas", { n: String(areas.length) }), tag: isUp ? "UP" : "NOVO" });
    }

    for (const row of perkRows) {
      const tagClass = row.tag === "NOVO" ? "vip-tag-new" : "vip-tag-up";
      html += `<div class="vip-perk-row"><span class="vip-perk-tag ${tagClass}">${row.tag}</span><span>${this.esc(row.label)}</span></div>`;
    }
    html += `</div>`;

    // 10.9BG — sem botão de compra: o tier vem dos pontos
    if (owned || isCurrent) {
      html += `<div class="vip-owned-badge">${i18n.t("vip.acquired")}</div>`;
    } else {
      html += `<div class="vip-locked-msg">🔒 ${this.esc(i18n.t("vip.pointsMissing", { n: String(Math.max(0, tier.cost - p.vipPoints)) }))}</div>`;
    }

    // ---- PASSE VIP (30 dias, multiplicadores exclusivos) ----
    const passCfg = GameConfig.vipPass;
    const now = Date.now();
    const passActive = p.passExpiresAt > now;
    const passRemainingMs = Math.max(0, p.passExpiresAt - now);
    const passDays = Math.ceil(passRemainingMs / (24 * 60 * 60 * 1000));
    const billing = GameConfig.billing.enabled; // 10.9FB — sem pagamento real: "Em breve"
    const passPrice = `R$ ${passCfg.priceBRL.toFixed(2).replace(".", ",")}`;
    html += `<div class="vip-section-title">${this.esc(i18n.t("vip.passTitle"))}</div>`;
    html += `<div class="vip-pass-card${passActive ? " active" : ""}">
      <div class="vip-pass-head">
        <span class="vip-pass-icon">🎫</span>
        <div class="vip-pass-info">
          <div class="vip-pass-name">${this.esc(i18n.t(passActive ? "vip.passName" : "vip.passNameOff"))}</div>
          <div class="vip-pass-sub">${passActive ? this.esc(i18n.t("vip.passRemaining", { days: String(passDays) })) : this.esc(i18n.t("vip.passDuration", { days: String(passCfg.durationDays) }))}</div>
        </div>
      </div>
      <div class="vip-pass-perks">
        <div class="vip-perk-row"><span class="vip-perk-tag vip-tag-new">+${passCfg.diamondBonus}💎</span><span>${this.esc(i18n.t("vip.pass.bonusDiamonds"))}</span></div>
        <div class="vip-perk-row"><span class="vip-perk-tag vip-tag-new">×${passCfg.xpMult}</span><span>${this.esc(i18n.t("vip.pass.xp"))}</span></div>
        <div class="vip-perk-row"><span class="vip-perk-tag vip-tag-new">×${passCfg.coinMult}</span><span>${this.esc(i18n.t("vip.pass.coin"))}</span></div>
        <div class="vip-perk-row"><span class="vip-perk-tag vip-tag-new">+${passCfg.dailyDiamonds}💎</span><span>${this.esc(i18n.t("vip.pass.daily"))}</span></div>
        <div class="vip-perk-row"><span class="vip-perk-tag vip-tag-new">+1⚔</span><span>${this.esc(i18n.t("vip.pass.weaponSlot"))}</span></div>
        <div class="vip-perk-row"><span class="vip-perk-tag vip-tag-new">${p.ownsOutfit(passCfg.outfitId) ? "✓" : "👗"}</span><span>${this.esc(i18n.t("vip.pass.outfit", { name: i18n.t(`outfit.${passCfg.outfitId}.name`) }))}</span></div>
      </div>
      <button class="btn vip-pass-buy primary" data-a="buy-pass"${billing ? "" : " disabled"}>${!billing ? `🔒 ${this.esc(i18n.t("vip.soon"))}` : passActive ? this.esc(i18n.t("vip.passRenew", { cost: passPrice })) : this.esc(i18n.t("vip.passBuy", { cost: passPrice }))}</button>
    </div>`;

    // ---- LOJA DE ATRIBUTOS (comprar Vida/Dano/Regen com 💎) ----
    html += `<div class="vip-section-title">${this.esc(i18n.t("attr.shopTitle"))}</div>`;
    html += `<div class="vip-packs-hint">${this.esc(i18n.t("attr.shopHint"))}</div>`;
    html += `<div class="attr-list">`;
    // 10.9J7 — custa 🪙 Fragmentos agora (era 💎 com desconto de tier VIP — dinheiro real
    // comprando atributo de combate direto). Sem desconto: o desconto do diamondShop é só pra 💎.
    for (const item of GameConfig.attrShop.items) {
      const cost = item.cost;
      const afford = p.coins >= cost;
      html += `<button class="attr-item${afford ? "" : " locked"}" data-attr="${item.id}">
        <span class="attr-icon">${item.icon}</span>
        <span class="attr-name">${this.esc(i18n.t(item.nameKey))}</span>
        <span class="attr-amt">+${item.amount}${item.kind === "regen" ? "/s" : ""}</span>
        <span class="attr-cost">🪙 ${cost}</span>
        ${p.shopCount(`attr:${item.id}`) > 0 ? `<span class="attr-owned">${this.esc(i18n.t("attr.boughtN", { n: String(p.shopCount(`attr:${item.id}`)) }))}</span>` : ""}
      </button>`;
    }
    html += `</div>`;

    // diamond shop section — gastar 💎 em itens do jogo
    html += `<div class="vip-section-title">${this.esc(i18n.t("dshop.title"))}</div>`;
    const disc = GameConfig.diamondShop.vipDiscountPct[cur] ?? 0;
    if (disc > 0) html += `<div class="vip-packs-hint">${this.esc(i18n.t("dshop.vipDiscount", { pct: String(disc) }))}</div>`;
    html += `<div class="dshop-list">`;
    let lastCat = ""; // 10.9DH — agrupa por categoria
    for (const item of GameConfig.diamondShop.items) {
      const cat = (item as { cat?: string }).cat ?? "";
      if (cat !== lastCat) { lastCat = cat; const col = this.collapsedCats.has(cat); html += `</div><button type="button" class="vip-section-title dshop-cat${col ? " collapsed" : ""}" data-cat="${cat}">${this.esc(i18n.t(`shop.cat.${cat}`))} <span class="dc-x">▾</span></button><div class="dshop-list${col ? " collapsed" : ""}">`; }
      // 10.9HA — mochila: preço sobe por compra e trava no teto (mostra "30 → 40")
      const bag = item.kind === "bag" ? bagUpgradeInfo(p.bagSlots, item.cost) : null;
      const cost = bag ? Math.max(1, Math.round(bag.price * (1 - disc / 100))) : Math.max(1, Math.round(item.cost * (1 - disc / 100)));
      const afford = bag?.full ? false : p.diamonds >= cost;
      const name = bag ? `${this.esc(i18n.t(item.nameKey))} · ${bag.capacity} → ${bag.next}` : this.esc(i18n.t(item.nameKey));
      html += `<button class="dshop-item${afford ? "" : " locked"}" data-item="${item.id}"${bag?.full ? " disabled" : ""}>
        <span class="dshop-icon">${item.icon}</span>
        <span class="dshop-name">${name}</span>
        <span class="dshop-cost">${bag?.full ? this.esc(i18n.t("dshop.bagFull")) : `💎 ${cost}${disc > 0 && !bag ? ` <s>${item.cost}</s>` : ""}`}</span>
      </button>`;
    }
    html += `</div>`;

    // 10.9BE — GRÁTIS ASSISTINDO (anúncio recompensado; VIP ganha direto)
    html += `<div class="vip-section-title">${this.esc(i18n.t("ads.sectionTitle"))}</div>`;
    html += `<div class="vip-packs-hint">${this.esc(i18n.t("ads.vipHint"))}</div>`;
    html += `<div class="dshop-list">`;
    const adBtn = (p: "freeDiamonds" | "xpBoost", icon: string, label: string): string => {
      const on = Ads.available(p);
      return `<button class="dshop-item ad-item${on ? "" : " locked"}" data-ad="${p}"${on ? "" : " disabled"}><span class="dshop-icon">${icon}</span><span class="dshop-name">${this.esc(label)}</span><span class="dshop-cost">${Vip.active ? "👑" : "📺"}</span></button>`;
    };
    html += adBtn("freeDiamonds", "💎", i18n.t("ads.freeDiamonds", { n: GameConfig.ads.freeDiamonds, left: Ads.left("freeDiamonds") }));
    html += adBtn("xpBoost", "⚡", i18n.t("ads.xpBoost", { left: Ads.left("xpBoost") }));
    html += `</div>`;

    // diamond packs section (dinheiro real)
    html += `<div class="vip-section-title">${this.esc(i18n.t("vip.packsTitle"))}</div>`;
    html += `<div class="vip-packs-hint">${this.esc(i18n.t("vip.packsHint"))}</div>`;
    // 10.9J8 — oferta de 1ª compra (reduz o medo do primeiro gasto). Some sozinha depois que a
    // conta já usou. 10.9L10 — Thiago: "12 mil não pode dar o dobro, no máximo 30%" — o bônus da
    // 1ª compra agora é POR PACOTE (`firstBonusPct`, padrão 100 = dobro; o Lendário é 30).
    const firstBuy = !p.firstPackBought;
    if (firstBuy) html += `<div class="vip-packs-hint vip-first-offer">${this.esc(i18n.t("vip.firstPackHint"))}</div>`;
    html += `<div class="vip-packs">`;
    for (const pack of GameConfig.diamondPacks) {
      const vipBonus = pack.vipBonusPct[cur] ?? 0;
      const total = Math.round(pack.diamonds * (1 + vipBonus / 100));
      const priceStr = `R$ ${pack.priceBRL.toFixed(2).replace(".", ",")}`;
      const tagTxt = pack.tag ? i18n.t("vip.pack.tag." + pack.tag) : "";
      const firstPct = (pack as { firstBonusPct?: number }).firstBonusPct ?? 100;
      const firstTotal = Math.round(total * (1 + firstPct / 100));
      // 10.9K3 — a 1ª compra só aparecia numa faixa de texto; o card mostrava o valor simples e o
      // jogador não via o que ia ganhar. Agora o card mostra o valor com bônus, riscando o normal.
      html += `<button class="vip-pack${pack.tag ? " tag-" + pack.tag : ""}${billing ? "" : " soon"}" data-pack="${pack.id}"${billing ? "" : " disabled"}>
        ${tagTxt ? `<span class="vip-pack-ribbon">${this.esc(tagTxt)}</span>` : ""}
        ${pack.bonusPct > 0 ? `<span class="vip-pack-freebonus">+${pack.bonusPct}% ${this.esc(i18n.t("vip.pack.bonusLabel"))}</span>` : ""}
        <span class="vip-pack-amt">💎 ${(firstBuy ? firstTotal : total).toLocaleString("pt-BR")}</span>
        ${firstBuy ? `<span class="vip-pack-first"><s>${total.toLocaleString("pt-BR")}</s> ${this.esc(firstPct >= 100 ? i18n.t("vip.pack.firstDouble") : i18n.t("vip.pack.firstPct", { pct: firstPct }))}</span>` : ""}
        ${vipBonus > 0 ? `<span class="vip-pack-bonus">VIP +${vipBonus}%</span>` : ""}
        <span class="vip-pack-price">${billing ? priceStr : `🔒 ${this.esc(i18n.t("vip.soon"))}`}</span>
      </button>`;
    }
    html += `</div>`;

    // close
    html += `<div class="menu-btns"><button class="btn primary" data-a="close">${this.esc(i18n.t("menu.back"))}</button></div>`;

    panel.innerHTML = html;

    // event listeners
    panel.querySelectorAll<HTMLButtonElement>('[data-a="close"]').forEach((b) => { b.onclick = () => this.h.onClose(); }); // 10.9EA — ✕ do topo + Voltar do rodape
    panel.querySelector<HTMLButtonElement>(".vip-prev")?.addEventListener("click", () => { this.viewTier = Math.max(0, this.viewTier - 1); this.render(); });
    panel.querySelector<HTMLButtonElement>(".vip-next")?.addEventListener("click", () => { this.viewTier = Math.min(tiers.length - 1, this.viewTier + 1); this.render(); });
    panel.querySelectorAll<HTMLButtonElement>(".dshop-cat").forEach((b) => b.addEventListener("click", () => { const c = b.dataset.cat!; if (this.collapsedCats.has(c)) this.collapsedCats.delete(c); else this.collapsedCats.add(c); this.render(); })); // 10.9DI
    // 10.9BJ — CONFIRMAÇÃO antes de comprar pacote / passe (Thiago: "dar uma mensagem de confirmação")
    panel.querySelectorAll<HTMLButtonElement>(".vip-pack").forEach(btn => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.pack;
        const pack = GameConfig.diamondPacks.find(x => x.id === id);
        if (!id || !pack) return;
        const bonus = pack.vipBonusPct[cur] ?? 0;
        // 10.9L10 — mesmo cálculo do Game.buyDiamondPack (firstBonusPct por pacote, padrão 100 = dobro)
        const firstPct = (pack as { firstBonusPct?: number }).firstBonusPct ?? 100;
        const total = Math.round(pack.diamonds * (1 + bonus / 100) * (firstBuy ? 1 + firstPct / 100 : 1));
        const priceStr = `R$ ${pack.priceBRL.toFixed(2).replace(".", ",")}`;
        const key = firstBuy ? (firstPct >= 100 ? "vip.confirmPackFirst" : "vip.confirmPackFirstPct") : "vip.confirmPack";
        this.confirm(i18n.t(key, { diamonds: total.toLocaleString("pt-BR"), price: priceStr, pct: firstPct }), () => this.h.onBuyPack(id));
      });
    });
    panel.querySelectorAll<HTMLButtonElement>(".dshop-item").forEach(btn => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.item;
        if (!id) return;
        if (btn.classList.contains("locked")) { this.h.onBuyItem(id); return; } // sem 💎: o jogo avisa
        // 10.9EJ — confirmacao tambem nos itens de DIAMANTE (antes so pacote/passe pediam)
        const name = btn.querySelector(".dshop-name")?.textContent ?? "";
        const price = btn.querySelector(".dshop-cost")?.textContent ?? "";
        this.confirm(i18n.t("shop.confirmBuy", { name, price }), () => this.h.onBuyItem(id), false);
      });
    });
    // 10.9ET — ATRIBUTOS PERMANENTES tambem confirmam antes de comprar (10.9J7: agora em 🪙, nao
    // 💎). No 10.9EJ so os itens de diamante ganhavam confirmacao; esta secao comprava no toque
    // (Thiago: "a confirmacao de compra com VIP nao ta funcionando"). Sem saldo segue direto (o
    // jogo avisa que falta).
    panel.querySelectorAll<HTMLButtonElement>(".attr-item").forEach(btn => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.attr;
        if (!id) return;
        if (btn.classList.contains("locked")) { this.h.onBuyAttr(id); return; }
        const name = `${btn.querySelector(".attr-name")?.textContent ?? ""} ${btn.querySelector(".attr-amt")?.textContent ?? ""}`.trim();
        const price = btn.querySelector(".attr-cost")?.textContent ?? "";
        this.confirm(i18n.t("shop.confirmBuy", { name, price }), () => this.h.onBuyAttr(id), false);
      });
    });
    panel.querySelector<HTMLButtonElement>('[data-a="buy-pass"]')?.addEventListener("click", () =>
      this.confirm(i18n.t("vip.confirmPass", { days: String(passCfg.durationDays), price: passPrice, diamonds: String(passCfg.diamondBonus) }), () => this.h.onBuyPass()));
    panel.querySelectorAll<HTMLButtonElement>(".ad-item").forEach(btn => {
      btn.addEventListener("click", () => { const p = btn.dataset.ad as "freeDiamonds" | "xpBoost" | undefined; if (p) this.h.onAd(p); });
    });
  }

  /** 10.9BJ — caixa de confirmação de compra (dentro do overlay VIP, por cima do painel). */
  private confirm(text: string, onYes: () => void, testNote = true): void {
    this.root.querySelector(".vip-confirm")?.remove();
    const box = document.createElement("div");
    box.className = "vip-confirm";
    box.innerHTML = `<div class="vip-confirm-box">
        <div class="vip-confirm-title">${this.esc(i18n.t("vip.confirmTitle"))}</div>
        <div class="vip-confirm-text">${this.esc(text)}</div>
        ${testNote ? `<div class="vip-confirm-note">${this.esc(i18n.t("vip.confirmTestNote"))}</div>` : ""}
        <div class="vip-confirm-btns">
          <button class="btn" data-a="no">${this.esc(i18n.t("vip.confirmNo"))}</button>
          <button class="btn primary" data-a="yes">${this.esc(i18n.t("vip.confirmYes"))}</button>
        </div>
      </div>`;
    box.addEventListener("click", (e) => { e.stopPropagation(); if (e.target === box) box.remove(); });
    box.querySelector('[data-a="no"]')!.addEventListener("click", () => box.remove());
    box.querySelector('[data-a="yes"]')!.addEventListener("click", () => { box.remove(); onYes(); });
    this.root.appendChild(box);
  }

  private esc(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
